
import { computed, defineComponent, PropType, reactive, ref, toRefs, watch } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { useQuasar } from 'quasar'
import { ComponentPropType, CtCatalogue, CtProduct, CtProp, CtStockItem } from '@/models/interfaces/content-type'
import DButton from '@/components/base/DButton.vue'
import CloseIcon from '@/components/Icons/CloseIcon.vue'
import TickIcon from '@/components/Icons/TickIcon.vue'
import PenIcon from '@/components/Icons/PenIcon.vue'
import AddIcon from '@/components/Icons/AddIcon.vue'
import { Entry, generateUUID, UUID } from '@Protium/vue-ozone-utils'
import ProductOptionCard from '@/pages/Products/components/ProductOptionCard.vue'
import { ContentType } from '@/services/directr'
import { cloneDeep } from 'lodash'
import draggable from 'vuedraggable'
import ProductVariantCard from '@/pages/Products/components/ProductVariantCard.vue'
import { ResolvedCtProduct } from '@/pages/Products/ProductDetail/Index.vue'
import { isUUID } from '@/utils'
import ListHeader from '@/components/ListHeader.vue'
import useContentModule from '@/pages/composables/useContentModule'
import useDiffSaveHandler, { SaveHooks } from '@/pages/Manage/Catalogues/composables/useDiffSaveHandler'
import Tooltip from '@/components/Tooltip.vue'
import LockClosedIcon from '@/components/Icons/LockClosedIcon.vue'

export default defineComponent({
  name: 'OptionsAndVariants',
  props: {
    allProps: {
      type: Array as PropType<Array<CtProp>>,
      default: () => []
    },
    product: {
      type: Object as PropType<ResolvedCtProduct>,
      default: () => ({})
    },
    productOptions: {
      type: Array as PropType<Array<CtProp>>,
      default: () => [],
    },
    productComponents: {
      type: Array as PropType<Array<CtStockItem>>,
      default: () => [],
    },
    productVariants: {
      type: Array as PropType<Array<ResolvedCtProduct>>,
      default: () => [],
    },
    locked: {
      type: Boolean,
      default: false
    }
  },
  components: {
    LockClosedIcon,
    Tooltip,
    ListHeader,
    ProductVariantCard,
    ProductOptionCard,
    AddIcon,
    PenIcon,
    TickIcon,
    CloseIcon,
    DButton,
    draggable
  },
  emits: ['save'],
  expose: ['propValues'],
  setup (props, { emit }) {

    const $store = useStore()
    const $router = useRouter()
    const $directr = $store.getters['Services/Directr']
    const $q = useQuasar()

    const state = reactive({
      editing: false,

      productOptionsEditState: (cloneDeep(props.productOptions) ?? []),
      productVariantsEditState: (cloneDeep(props.productVariants) ?? []),
      productsToDelete: [] as { id: UUID, deleteCascade: { catalogue_products: boolean, stock_items: boolean, variants: boolean }, }[],

      drag: { options: false, variants: false },
      dragOptions: {
        animation: 200,
        group: 'description',
        disabled: false,
        ghostClass: 'ghost'
      },
    })

    const propValues = computed(() => (
      !props.productComponents ? [] :
        Object.fromEntries(
          Object.entries<[string, any[]]>(
            props.productComponents
              .concat(state.productVariantsEditState?.flatMap(v => v.components) ?? [])
              .reduce((prev, cur) => {
                for (const [propId, propValue] of Object.entries(cur?.props ?? {})) {
                  if (propId in prev) {
                    prev[propId].add(propValue)
                  } else {
                    prev[propId] = new Set([
                      propValue,
                    ])
                  }
                }
                return prev
              }, {})
          ).map(([key, value]) => [key, Array.from(value).sort()])
        )
    ))

    watch(() => props.productOptions, () => state.productOptionsEditState = cloneDeep(props.productOptions) ?? [])
    watch(() => props.productVariants, () => state.productVariantsEditState = cloneDeep(props.productVariants) ?? [])

    const productOptionIdsEditState = computed(() => (
      state.productOptionsEditState.map(po => po.sys.id)
    ))

    const propChoices = computed(() => (
      props.allProps
        .concat(state.productOptionsEditState.filter(po => po.sys.draft))
        .map(p => ({ label: p.name, value: p.sys.id, alreadyUsed: productOptionIdsEditState.value.includes(p.sys.id) }))
    ))

    const addOption = () => {
      state.productOptionsEditState.push({
        id: '',
        value: '',
        name: '',
        sys: {
          type: 'prop',
          draft: true,
          id: generateUUID()
        }
      })
    }

    const updateProductOption = (newValue, index) => {
      if (isUUID(newValue)) {
        const newOption = props.allProps.find(p => p.sys.id === newValue)
        if (newOption) {
          state.productOptionsEditState.splice(index, 1, newOption)
        }
      } else {
        const newOption = props.allProps.find(p => p.name === newValue)
        if (newOption) {
          state.productOptionsEditState.splice(index, 1, newOption)
        } else {
          state.productOptionsEditState.splice(index, 1, {
            id: '',
            value: '',
            name: newValue,
            sys: {
              type: 'prop',
              draft: true,
              id: generateUUID()
            }
          })
        }
      }
    }

    const removeProductOption = (index) => {
      $q.dialog({
        title: 'Delete product option?',
        message: 'Are you sure?',
        cancel: true,
        persistent: false
      }).onOk(() => {
        if (!state.productOptionsEditState) return
        state.productOptionsEditState.splice(index, 1)
      })
    }

    const updateProductVariant = (index, variant) => {
      state.productVariantsEditState[index] = variant
    }

    const unlinkProductVariant = (index) => {
      $q.dialog({
        title: 'Unlink product variant?',
        message: 'Do you want to delete the product variant entirely?',
        cancel: true,
        persistent: false,
        options: {
          type: 'checkbox',
          model: [],
          items: [
            { label: 'Delete product', value: 'delete', }
          ]
        }
      }).onOk((data: string[]) => {
        void (async () => {
          if (!state.productVariantsEditState) return
          if (data.includes('delete')) {
            state.productsToDelete.push({
              id: state.productVariantsEditState[index].sys.id,
              deleteCascade: await cascadeDeleteDialog()
            })
          }
          state.productVariantsEditState.splice(index, 1)
        })()
      })
    }

    const cascadeDeleteDialog = (): Promise<{ catalogue_products: boolean; stock_items: boolean; variants: boolean; }> => {
      const result = { catalogue_products: false, stock_items: false, variants: false }
      return new Promise((res, _) => {
        $q.dialog({
          title: 'Before you delete the product...',
          message: 'The product will be deleted but it may have connected data. Select which data connected to the product you want to delete.',
          options: {
            type: 'checkbox',
            model: [],
            items: [
              { label: 'Catalogue products', value: 'catalogue_products' },
              { label: 'Stock items', value: 'stock_items' },
              { label: 'Variants', value: 'variants' }
            ],
          },
          cancel: false,
          persistent: false
        }).onOk((data: string[]) => {
          res(data.reduce((obj, cur) => {
            obj[cur] = true
            return obj
          }, result))
        }).onCancel(() => res(result))
          .onDismiss(() => res(result))
      })
    }

    const updateOptionValue = (index, x, y) => {
      if (
        !state.productVariantsEditState ||
        !state.productVariantsEditState?.[index]?.components?.length
      ) return
      state.productVariantsEditState[index].components[0].props = {
        ...state.productVariantsEditState[index].components[0].props,
        [x]: y
      }
    }

    const addVariant = () => {
      state.productVariantsEditState.push({
        ...props.product,
        sku: undefined,
        ean: [],
        is_parent: false,
        variants: [],
        components: props.product.components.length ? [
          {
            ...props.product.components[0],
            sku: undefined,
            ean: [],
            props: {
              ...Object.fromEntries(state.productOptionsEditState.map(po => [po.sys.id, ''])),
              ...props.product.components[0].props
            },
            sys: {
              ...props.product.components[0].sys,
              id: generateUUID(),
              draft: true,
            }
          },
          ...props.product.components.slice(1)
        ] : [],
        sys: {
          id: generateUUID(),
          type: 'product',
          draft: true,
        }
      })
    }

    const cancel = () => {
      state.productOptionsEditState = cloneDeep(props.productOptions) ?? []
      state.productVariantsEditState = cloneDeep(props.productVariants) ?? []
      state.editing = false
    }

    const save = () => {
      emit('save', state.productOptionsEditState, state.productVariantsEditState, state.productsToDelete)
      state.editing = false
    }

    return {
      ...toRefs(state),
      propValues,
      propChoices,
      productOptionIdsEditState,
      addOption,
      addVariant,
      updateProductOption,
      removeProductOption,
      updateProductVariant,
      unlinkProductVariant,
      updateOptionValue,
      cancel,
      save,
    }
  },
})
